/**
 * Phase 4 Slice 0 — battle roles, counters, ground/season tags, Option A resolve.
 *
 * Composition-first: flat MAA/host pools + lord favorite-role leans become
 * role weight vectors. No train/promote factory (that is Phase 4.5).
 *
 * Paper SoT: CALL_TO_ARMS_PAPER_RULES_PACK matchup chart + Option A freeze
 * (preview + auto; higher score wins light / solid / crush).
 */
import type { Season } from "./clock.ts";
import type { Biome } from "./map.ts";
import { tileStatsFor } from "./tileStats.ts";

// ── Roles (paper eight-role roster) ─────────────────────────────────────────

export type TroopRole =
  | "levy"
  | "spear"
  | "maa"
  | "archer"
  | "crossbow"
  | "lightHorse"
  | "heavyHorse"
  | "siege";

export const TROOP_ROLES: readonly TroopRole[] = [
  "levy",
  "spear",
  "maa",
  "archer",
  "crossbow",
  "lightHorse",
  "heavyHorse",
  "siege",
] as const;

export const ROLE_LABELS: Record<TroopRole, string> = {
  levy: "Levy foot",
  spear: "Spearmen",
  maa: "Men-at-arms",
  archer: "Archers",
  crossbow: "Crossbows",
  lightHorse: "Light horse",
  heavyHorse: "Heavy horse",
  siege: "Siege / works",
};

/** Soft-spot one-liners for preview chrome. */
export const ROLE_SOFT_SPOT: Record<TroopRole, string> = {
  levy: "Breaks vs heavy shock and sustained fire",
  spear: "Slow; woods and long bow fire without cover hurt",
  maa: "Costly; mud/swamp and mass + shot wear them",
  archer: "Die if horse or hard foot reach them clean",
  crossbow: "Same if closed on; rain/mud drafts worse",
  lightHorse: "Spears and tight rough ground blunt them",
  heavyHorse: "Spears, stakes, steep hills, woods; needs room",
  siege: "Weak in pure open meeting fights; slow in winter",
};

/**
 * Matchup chart (paper): row fighting column.
 * +1 = row favored · 0 = even · -1 = row in trouble.
 * Siege column specials (defended / staked / packed) applied in matchupValue().
 */
export const MATCHUP: Record<TroopRole, Record<TroopRole, -1 | 0 | 1>> = {
  levy: {
    levy: 0,
    spear: -1,
    maa: -1,
    archer: 0,
    crossbow: 0,
    lightHorse: -1,
    heavyHorse: -1,
    siege: 0,
  },
  spear: {
    levy: 1,
    spear: 0,
    maa: 0,
    archer: 0,
    crossbow: 0,
    lightHorse: 1,
    heavyHorse: 1,
    siege: 0,
  },
  maa: {
    levy: 1,
    spear: 0,
    maa: 0,
    archer: 1,
    crossbow: 0,
    lightHorse: 1,
    heavyHorse: 0,
    siege: 0,
  },
  archer: {
    levy: 1,
    spear: 0,
    maa: -1,
    archer: 0,
    crossbow: 0,
    lightHorse: -1,
    heavyHorse: -1,
    siege: 1,
  },
  crossbow: {
    levy: 1,
    spear: 0,
    maa: 1,
    archer: 0,
    crossbow: 0,
    lightHorse: -1,
    heavyHorse: 0,
    siege: 1,
  },
  lightHorse: {
    levy: 1,
    spear: -1,
    maa: -1,
    archer: 1,
    crossbow: 1,
    lightHorse: 0,
    heavyHorse: -1,
    siege: 1,
  },
  heavyHorse: {
    levy: 1,
    spear: -1,
    maa: 0,
    archer: 1,
    crossbow: 0,
    lightHorse: 1,
    heavyHorse: 0,
    siege: -1,
  },
  siege: {
    levy: 0,
    spear: 0,
    maa: 0,
    archer: -1,
    crossbow: -1,
    lightHorse: -1,
    heavyHorse: -1,
    siege: 0,
  },
};

export type MatchupOpts = {
  /** Stakes / towers / prepared field — siege and anti-charge matter. */
  preparedField?: boolean;
};

/**
 * Raw chart cell with paper footnotes:
 * - Archers/XB vs siege only “+ if defended” → need preparedField
 * - Light horse vs siege “+ if caught packed” → preparedField
 * - Heavy horse vs siege “− if staked” → preparedField; else even/absent
 * - Siege in pure meeting fight → treat as weak (row siege dampened)
 */
export function matchupValue(
  row: TroopRole,
  col: TroopRole,
  opts: MatchupOpts = {},
): number {
  let v = MATCHUP[row][col] as number;
  const prep = !!opts.preparedField;

  if ((row === "archer" || row === "crossbow") && col === "siege") {
    v = prep ? 1 : 0;
  }
  if (row === "lightHorse" && col === "siege") {
    v = prep ? 1 : 0;
  }
  if (row === "heavyHorse" && col === "siege") {
    v = prep ? -1 : 0;
  }
  // Siege kit barely counts in a pure open meeting
  if (row === "siege" && !prep) {
    v = Math.min(v, 0) - 0.5;
  }
  if (col === "siege" && !prep && row !== "siege") {
    // enemy siege absent → neutral vs that slice
    if (MATCHUP[row].siege !== 0 && row !== "archer" && row !== "crossbow") {
      /* keep chart */
    }
  }
  return v;
}

// ── Ground ──────────────────────────────────────────────────────────────────

export type GroundTag =
  | "open"
  | "hills"
  | "forest"
  | "river"
  | "mud"
  | "town"
  | "mountains"
  | "marsh"
  | "coast";

export const GROUND_LABELS: Record<GroundTag, string> = {
  open: "Open plain",
  hills: "Hills",
  forest: "Forest",
  river: "River crossing",
  mud: "Mud / soft ground",
  town: "Town / walls",
  mountains: "Mountains",
  marsh: "Marsh",
  coast: "Coast",
};

/** Map hex biome → fight ground tag (one main tag). */
export function groundFromBiome(biome: Biome): GroundTag {
  switch (biome) {
    case "plains":
      return "open";
    case "forest":
      return "forest";
    case "hills":
    case "foothills":
      return "hills";
    case "mountains":
      return "mountains";
    case "marsh":
      return "marsh";
    case "river":
      return "river";
    case "coast":
      return "coast";
    case "water":
      return "open"; // should not fight here; fallback
    default: {
      const _exhaustive: never = biome;
      return _exhaustive;
    }
  }
}

/**
 * Role multiplier on this ground (1 = neutral).
 * Paper: open favors horse + archery lanes; hills brace defenders/spears/archers
 * and blunt horse; forest hurts heavy horse/siege; river funnels attackers;
 * mud hurts horse/XB; town favors defenders/XB/works.
 */
export function roleGroundFactor(
  role: TroopRole,
  ground: GroundTag,
  side: "attacker" | "defender",
): number {
  const def = side === "defender";
  switch (ground) {
    case "open":
      if (role === "heavyHorse") return 1.2;
      if (role === "lightHorse") return 1.1;
      if (role === "archer" || role === "crossbow") return 1.1;
      if (role === "siege") return 0.85;
      return 1;
    case "hills":
    case "mountains": {
      const hill = ground === "mountains" ? 1.15 : 1;
      if (role === "archer" && def) return 1.2 * hill;
      if (role === "spear" && def) return 1.15 * hill;
      if (role === "maa" && def) return 1.05 * hill;
      if (role === "heavyHorse") return 0.75;
      if (role === "lightHorse") return 0.85;
      if (!def && (role === "levy" || role === "maa")) return 0.95;
      return def ? 1.05 * hill : 1;
    }
    case "forest":
      if (role === "heavyHorse") return 0.55;
      if (role === "lightHorse") return 0.8;
      if (role === "siege") return 0.5;
      if (role === "levy" || role === "spear" || role === "archer") return 1.15;
      return 1;
    case "river":
      if (def) {
        if (role === "spear" || role === "maa" || role === "crossbow") return 1.2;
        return 1.1;
      }
      if (role === "heavyHorse" || role === "lightHorse") return 0.7;
      if (role === "siege") return 0.75;
      return 0.9;
    case "mud":
    case "marsh":
      if (role === "heavyHorse") return 0.55;
      if (role === "lightHorse") return 0.7;
      if (role === "crossbow") return 0.8;
      if (role === "siege") return 0.7;
      if (role === "spear" || role === "levy" || role === "maa") return 1.1;
      return 1;
    case "town":
      if (def) {
        if (role === "crossbow" || role === "archer") return 1.25;
        if (role === "siege") return 1.15;
        if (role === "spear" || role === "maa") return 1.15;
        return 1.1;
      }
      if (role === "heavyHorse" || role === "lightHorse") return 0.5;
      if (role === "siege") return 1.2;
      return 0.9;
    case "coast":
      if (role === "lightHorse" || role === "archer") return 1.05;
      return 1;
    default:
      return 1;
  }
}

export function groundNote(ground: GroundTag, _season?: Season): string {
  const base = GROUND_LABELS[ground];
  switch (ground) {
    case "open":
      return `${base} — horse and archery lanes smile; cover is scarce.`;
    case "hills":
      return `${base} — defenders brace; horse charge less clean.`;
    case "mountains":
      return `${base} — high ground favors the holder; charges stall.`;
    case "forest":
      return `${base} — light foot and ambush; heavy horse and wide kits suffer.`;
    case "river":
      return `${base} — ford funnels the attacker; horse disorders.`;
    case "mud":
      return `${base} — spears stand; heavy horse and crossbows hate it.`;
    case "marsh":
      return `${base} — bog slows all; horse and engines suffer most.`;
    case "town":
      return `${base} — walls love shot and works; pure horse is wasted.`;
    case "coast":
      return `${base} — open shore march; little natural cover.`;
    default:
      return base;
  }
}

// ── Season ──────────────────────────────────────────────────────────────────

export function seasonFightFactor(season: Season): {
  attacker: number;
  defender: number;
  note: string;
} {
  switch (season) {
    case "Spring":
      return {
        attacker: 0.97,
        defender: 1,
        note: "Spring — soft ground possible; campaigns just thawing.",
      };
    case "Summer":
      return {
        attacker: 1.05,
        defender: 1,
        note: "Summer — best campaign weather; horse and roads strong.",
      };
    case "Autumn":
      return {
        attacker: 0.95,
        defender: 1.02,
        note: "Autumn — last good window; mud risk rises.",
      };
    case "Winter":
      return {
        attacker: 0.8,
        defender: 1.05,
        note: "Winter — bad fighting season; prefer not to force pitched battle.",
      };
    default: {
      const _e: never = season;
      return _e;
    }
  }
}

/** Autumn/Spring can overlay mud on open/coast fights (paper leftover risk). */
export function applySeasonGround(
  ground: GroundTag,
  season: Season,
): GroundTag {
  if (
    (season === "Autumn" || season === "Spring") &&
    (ground === "open" || ground === "coast")
  ) {
    // Soft bias only when already open — still labeled open for teaching;
    // mud factors applied via season attacker penalty. Explicit mud tag
    // reserved for marsh or caller override.
  }
  if (season === "Autumn" && ground === "marsh") return "mud";
  return ground;
}

// ── Composition proxy (no factory) ──────────────────────────────────────────

export type RoleWeights = Record<TroopRole, number>;

export interface ForceComposition {
  /** Headcount (garrison/host spears). */
  total: number;
  /** Role shares; should sum ≈ 1. */
  weights: RoleWeights;
  /** Named general tip (small additive on score). */
  generalBonus?: number;
  /** 0–1 readiness / supply / fidelity tease. */
  readiness?: number;
  /** Display name for preview. */
  label?: string;
  /** Lord favorite roles (for chrome). */
  favorites?: TroopRole[];
}

export function emptyWeights(): RoleWeights {
  return {
    levy: 0,
    spear: 0,
    maa: 0,
    archer: 0,
    crossbow: 0,
    lightHorse: 0,
    heavyHorse: 0,
    siege: 0,
  };
}

export function normalizeWeights(
  partial: Partial<Record<TroopRole, number>>,
): RoleWeights {
  const w = emptyWeights();
  let sum = 0;
  for (const r of TROOP_ROLES) {
    const v = Math.max(0, partial[r] ?? 0);
    w[r] = v;
    sum += v;
  }
  if (sum <= 0) {
    w.maa = 1;
    return w;
  }
  for (const r of TROOP_ROLES) w[r] = w[r]! / sum;
  return w;
}

/**
 * Build a composition from flat host count + 1–3 lord favorite roles.
 * Spine stays MAA/levy clay; favorites pull mass into signature types.
 */
export function compositionFromHost(opts: {
  total: number;
  favorites?: readonly TroopRole[];
  /** Baseline clay when no favorites (default men-at-arms). */
  baseRole?: TroopRole;
  label?: string;
  generalBonus?: number;
  readiness?: number;
}): ForceComposition {
  const total = Math.max(0, Math.floor(opts.total));
  const base = opts.baseRole ?? "maa";
  const favs = (opts.favorites ?? []).filter((r, i, a) => a.indexOf(r) === i).slice(0, 3);

  const partial: Partial<Record<TroopRole, number>> = {};
  if (favs.length === 0) {
    partial[base] = 0.7;
    partial.levy = 0.3;
  } else {
    // Clay remains majority; each favorite takes a signature slice
    const favShare = Math.min(0.55, 0.18 * favs.length + 0.12);
    const per = favShare / favs.length;
    for (const f of favs) partial[f] = (partial[f] ?? 0) + per;
    const clay = 1 - favShare;
    partial[base] = (partial[base] ?? 0) + clay * 0.65;
    partial.levy = (partial.levy ?? 0) + clay * 0.35;
  }

  return {
    total,
    weights: normalizeWeights(partial),
    label: opts.label,
    generalBonus: opts.generalBonus ?? 0,
    readiness: opts.readiness ?? 1,
    favorites: favs.length ? [...favs] : undefined,
  };
}

export function describeComposition(force: ForceComposition): string {
  const parts = TROOP_ROLES.filter((r) => (force.weights[r] ?? 0) >= 0.05)
    .sort((a, b) => force.weights[b]! - force.weights[a]!)
    .map((r) => `${Math.round(force.weights[r]! * 100)}% ${ROLE_LABELS[r]}`);
  const name = force.label ? `${force.label}: ` : "";
  return `${name}${force.total} spears — ${parts.join(", ") || "empty"}`;
}

// ── Option A scoring ────────────────────────────────────────────────────────

export type BattleSide = "attacker" | "defender" | "draw";
export type VictoryBand = "crush" | "solid" | "light" | "draw";
export type LoserFate = "retreat" | "shatter" | "hold";

export interface BattleInput {
  attacker: ForceComposition;
  defender: ForceComposition;
  biome: Biome;
  season: Season;
  /** Prepared stakes/towers — enables siege footnotes. */
  preparedField?: boolean;
  /** Override ground tag (tests / special fields). */
  ground?: GroundTag;
}

export interface BattlePreview {
  attackerScore: number;
  defenderScore: number;
  delta: number;
  ground: GroundTag;
  groundNote: string;
  seasonNote: string;
  matchupNotes: string[];
  compositionLines: string[];
  lines: string[];
  projectedWinner: BattleSide;
  projectedBand: VictoryBand;
}

export interface BattleResult extends BattlePreview {
  winner: BattleSide;
  band: VictoryBand;
  /** Fraction of headcount lost (0–1). */
  attackerLossFrac: number;
  defenderLossFrac: number;
  loserFate: LoserFate;
  summary: string;
}

/** Numbers curve — blobs help but do not auto-win (paper). */
export function numbersFactor(total: number): number {
  if (total <= 0) return 0;
  // sqrt keeps 4× men from being 4× score
  return Math.sqrt(total);
}

function pairMatchupScore(
  a: RoleWeights,
  b: RoleWeights,
  opts: MatchupOpts,
): number {
  let s = 0;
  for (const r of TROOP_ROLES) {
    const wr = a[r] ?? 0;
    if (wr <= 0) continue;
    for (const c of TROOP_ROLES) {
      const wc = b[c] ?? 0;
      if (wc <= 0) continue;
      s += wr * wc * matchupValue(r, c, opts);
    }
  }
  return s;
}

/**
 * Top teaching matchup lines (spears stop horse; horse runs archers; etc.).
 */
export function teachingMatchupNotes(
  attacker: ForceComposition,
  defender: ForceComposition,
  opts: MatchupOpts = {},
): string[] {
  const notes: string[] = [];
  const aw = attacker.weights;
  const dw = defender.weights;

  const spearVsHh =
    (aw.spear ?? 0) * (dw.heavyHorse ?? 0) * matchupValue("spear", "heavyHorse", opts) +
    (dw.spear ?? 0) * (aw.heavyHorse ?? 0) * matchupValue("spear", "heavyHorse", opts);
  if (spearVsHh > 0.02) {
    notes.push("Spears stop heavy horse if the line holds.");
  }

  const horseVsArch =
    (aw.lightHorse ?? 0) * (dw.archer ?? 0) +
    (aw.heavyHorse ?? 0) * (dw.archer ?? 0) +
    (dw.lightHorse ?? 0) * (aw.archer ?? 0) +
    (dw.heavyHorse ?? 0) * (aw.archer ?? 0);
  if (horseVsArch > 0.04) {
    notes.push("Horse runs down unprotected archers.");
  }

  const xbVsMaa =
    (aw.crossbow ?? 0) * (dw.maa ?? 0) * matchupValue("crossbow", "maa", opts) +
    (dw.crossbow ?? 0) * (aw.maa ?? 0) * matchupValue("crossbow", "maa", opts);
  if (xbVsMaa > 0.02) {
    notes.push("Crossbows punch armored foot at range.");
  }

  return notes.slice(0, 3);
}

function effectiveRoleMass(
  force: ForceComposition,
  ground: GroundTag,
  side: "attacker" | "defender",
): number {
  let mass = 0;
  for (const r of TROOP_ROLES) {
    const w = force.weights[r] ?? 0;
    if (w <= 0) continue;
    mass += w * roleGroundFactor(r, ground, side);
  }
  return mass;
}

function scoreSide(
  self: ForceComposition,
  enemy: ForceComposition,
  side: "attacker" | "defender",
  ground: GroundTag,
  season: Season,
  preparedField: boolean,
  defenseMod: number,
): number {
  const seasonFx = seasonFightFactor(season);
  const seasonMul = side === "attacker" ? seasonFx.attacker : seasonFx.defender;
  const ready = clamp01(self.readiness ?? 1);
  const nums = numbersFactor(self.total) * ready;
  const groundMass = effectiveRoleMass(self, ground, side);
  const match = pairMatchupScore(self.weights, enemy.weights, { preparedField });
  // match is in [-1,1] expected; scale so counters teach louder than clay noise
  const matchPart = match * 8;
  const groundPart = (groundMass - 1) * numbersFactor(self.total) * 0.85;
  let score = (nums + groundPart + matchPart) * seasonMul;
  score += self.generalBonus ?? 0;
  if (side === "defender") {
    score += defenseMod * 1.25;
  }
  // Empty force collapses
  if (self.total <= 0) score = -999;
  return score;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

export function bandFromDelta(delta: number): VictoryBand {
  const ad = Math.abs(delta);
  if (ad < 1.25) return "draw";
  if (ad < 3.5) return "light";
  if (ad < 7) return "solid";
  return "crush";
}

export function winnerFromDelta(delta: number): BattleSide {
  if (Math.abs(delta) < 1.25) return "draw";
  return delta > 0 ? "attacker" : "defender";
}

function lossFractions(
  band: VictoryBand,
  winner: BattleSide,
): { attacker: number; defender: number; fate: LoserFate } {
  if (winner === "draw" || band === "draw") {
    return { attacker: 0.12, defender: 0.12, fate: "hold" };
  }
  const winLight = band === "light" ? 0.08 : band === "solid" ? 0.12 : 0.18;
  const loseFrac = band === "light" ? 0.18 : band === "solid" ? 0.32 : 0.55;
  const fate: LoserFate = band === "crush" ? "shatter" : "retreat";
  if (winner === "attacker") {
    return { attacker: winLight, defender: loseFrac, fate };
  }
  return { attacker: loseFrac, defender: winLight, fate };
}

export function previewBattle(input: BattleInput): BattlePreview {
  const ground0 = input.ground ?? groundFromBiome(input.biome);
  const ground = applySeasonGround(ground0, input.season);
  const prepared = !!input.preparedField;
  const defMod = tileStatsFor(input.biome).defenseMod;
  const seasonFx = seasonFightFactor(input.season);

  const attackerScore = scoreSide(
    input.attacker,
    input.defender,
    "attacker",
    ground,
    input.season,
    prepared,
    defMod,
  );
  const defenderScore = scoreSide(
    input.defender,
    input.attacker,
    "defender",
    ground,
    input.season,
    prepared,
    defMod,
  );
  const delta = attackerScore - defenderScore;
  const projectedWinner = winnerFromDelta(delta);
  const projectedBand = bandFromDelta(delta);
  const matchupNotes = teachingMatchupNotes(input.attacker, input.defender, {
    preparedField: prepared,
  });
  const gNote = groundNote(ground, input.season);
  const compositionLines = [
    describeComposition(input.attacker),
    describeComposition(input.defender),
  ];

  const lines: string[] = [
    ...compositionLines,
    gNote,
    seasonFx.note,
    ...matchupNotes,
  ];

  if (input.attacker.generalBonus && input.attacker.generalBonus > 0) {
    lines.push(`Attacker general tips the line (+${input.attacker.generalBonus}).`);
  }
  if (input.defender.generalBonus && input.defender.generalBonus > 0) {
    lines.push(`Defender general tips the line (+${input.defender.generalBonus}).`);
  }

  const lean =
    projectedWinner === "draw"
      ? "Even — coin-tip or blood draw."
      : projectedWinner === "attacker"
        ? `Lean attacker (${projectedBand}).`
        : `Lean defender (${projectedBand}).`;
  lines.push(lean);

  return {
    attackerScore: round2(attackerScore),
    defenderScore: round2(defenderScore),
    delta: round2(delta),
    ground,
    groundNote: gNote,
    seasonNote: seasonFx.note,
    matchupNotes,
    compositionLines,
    lines,
    projectedWinner,
    projectedBand,
  };
}

/** Option A — preview inputs then auto-resolve (deterministic from scores). */
export function resolveBattle(input: BattleInput): BattleResult {
  const preview = previewBattle(input);
  const winner = preview.projectedWinner;
  const band = preview.projectedBand;
  const { attacker, defender, fate } = lossFractions(band, winner);

  const aName = input.attacker.label ?? "Attacker";
  const dName = input.defender.label ?? "Defender";
  let summary: string;
  if (winner === "draw") {
    summary = `Draw in the ${GROUND_LABELS[preview.ground].toLowerCase()} — both sides bloodied, neither broken.`;
  } else if (winner === "attacker") {
    const fateWord = fate === "shatter" ? "shatters" : "retreats";
    summary = `${aName} wins a ${band} victory — ${dName} ${fateWord}.`;
  } else {
    const fateWord = fate === "shatter" ? "shatters" : "retreats";
    summary = `${dName} holds a ${band} victory — ${aName} ${fateWord}.`;
  }

  return {
    ...preview,
    winner,
    band,
    attackerLossFrac: attacker,
    defenderLossFrac: defender,
    loserFate: fate,
    summary,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Apply loss fractions to headcounts (post-battle remaining). */
export function applyLosses(
  total: number,
  lossFrac: number,
): { remaining: number; lost: number } {
  const lost = Math.min(total, Math.max(0, Math.round(total * lossFrac)));
  return { remaining: Math.max(0, total - lost), lost };
}

// ── Teaching scenarios (paper first two fights) ─────────────────────────────

/** Scenario 1: spears stop heavy horse on open ground. */
export function scenarioSpearsStopHorse(): BattleInput {
  return {
    attacker: {
      total: 100,
      label: "Horse lords",
      weights: normalizeWeights({ heavyHorse: 0.7, maa: 0.2, levy: 0.1 }),
    },
    defender: {
      total: 100,
      label: "Spear wall",
      weights: normalizeWeights({ spear: 0.75, maa: 0.15, levy: 0.1 }),
    },
    biome: "plains",
    season: "Summer",
  };
}

/** Scenario 2: horse runs down unprotected archers. */
export function scenarioHorseRunsArchers(): BattleInput {
  return {
    attacker: {
      total: 80,
      label: "Riders",
      weights: normalizeWeights({
        lightHorse: 0.45,
        heavyHorse: 0.35,
        maa: 0.2,
      }),
    },
    defender: {
      total: 80,
      label: "Bow line",
      weights: normalizeWeights({ archer: 0.7, levy: 0.3 }),
    },
    biome: "plains",
    season: "Summer",
  };
}

/**
 * Scenario 3: ground flips a row — same horse vs spears, but forest
 * blunts the charge so spears (or equal mass) hold cleaner.
 */
export function scenarioGroundFlipsHorse(): {
  open: BattleInput;
  forest: BattleInput;
} {
  const attacker = {
    total: 120,
    label: "Knights",
    weights: normalizeWeights({ heavyHorse: 0.65, maa: 0.25, levy: 0.1 }),
  };
  const defender = {
    total: 90,
    label: "Thin spears",
    weights: normalizeWeights({ spear: 0.6, levy: 0.3, maa: 0.1 }),
  };
  return {
    open: { attacker, defender, biome: "plains", season: "Summer" },
    forest: { attacker, defender, biome: "forest", season: "Summer" },
  };
}
